/**
 * FolderTree Component
 * Recursive tree view for hierarchical folder navigation
 * Features: Expand/collapse, selection, context menu, move folder
 */

import React, { useState, useEffect } from 'react';
import { Folder } from '../services/api';
import './FolderTree.css';

interface FolderTreeProps {
  folders: Folder[];
  selectedFolderId: string | null;
  onSelectFolder: (folderId: string) => void;
  onCreateSubfolder: (parentId: string) => void;
  onRenameFolder: (folderId: string) => void;
  onDeleteFolder: (folderId: string) => void;
  onMoveFolder?: (folderId: string, newParentId: string | null) => void;
  onSubmitReview?: (folderId: string) => void;
  onReviewAction?: (folderId: string) => void;
  onSetEpicId?: (folderId: string, currentEpicId?: string) => void;
  currentUserEmail?: string;
  title?: string;
}

interface FolderNodeProps {
  folder: Folder;
  level: number;
  selectedFolderId: string | null;
  onSelectFolder: (folderId: string) => void;
  onCreateSubfolder: (parentId: string) => void;
  onRenameFolder: (folderId: string) => void;
  onDeleteFolder: (folderId: string) => void;
  onMoveFolder?: (folderId: string) => void;
  onSubmitReview?: (folderId: string) => void;
  onReviewAction?: (folderId: string) => void;
  onSetEpicId?: (folderId: string, currentEpicId?: string) => void;
  currentUserEmail?: string;
  children: Folder[];
  allFolders: Folder[];
  expandedIds: Set<string>;
}

// Compute recursive count for a folder
function getRecursiveCount(folderId: string, allFolders: Folder[], countKey: 'test_case_count' | 'test_scenario_count'): number {
  let total = 0;
  const queue = [folderId];
  while (queue.length > 0) {
    const current = queue.shift()!;
    const folder = allFolders.find(f => f.id === current);
    if (folder) {
      total += (folder[countKey] as number) || 0;
      allFolders.filter(f => f.parent_id === current).forEach(c => queue.push(c.id));
    }
  }
  return total;
}

// Build set of ancestor IDs for a given folder (to auto-expand the path)
function getAncestorIds(folderId: string | null, allFolders: Folder[]): Set<string> {
  const ancestors = new Set<string>();
  if (!folderId) return ancestors;
  let current = allFolders.find(f => f.id === folderId);
  while (current?.parent_id) {
    const parentId = current.parent_id;
    ancestors.add(parentId);
    current = allFolders.find(f => f.id === parentId);
  }
  return ancestors;
}

// Check if targetId is a descendant of sourceId
function isDescendant(sourceId: string, targetId: string, allFolders: Folder[]): boolean {
  const queue = [sourceId];
  while (queue.length > 0) {
    const current = queue.shift()!;
    if (current === targetId) return true;
    allFolders.filter(f => f.parent_id === current).forEach(c => queue.push(c.id));
  }
  return false;
}

const REVIEW_BADGE: Record<string, { label: string; color: string }> = {
  'Draft':             { label: 'Draft',            color: '#6b7280' },
  'In Review':         { label: 'In Review',         color: '#29b6f6' },
  'Approved':          { label: 'Approved',          color: '#2ecc71' },
  'Changes Requested': { label: 'Change Requested',  color: '#f05a6e' },
};

const FolderNode: React.FC<FolderNodeProps> = ({
  folder, level, selectedFolderId, onSelectFolder,
  onCreateSubfolder, onRenameFolder, onDeleteFolder, onMoveFolder,
  onSubmitReview, onReviewAction, onSetEpicId, currentUserEmail,
  children, allFolders, expandedIds
}) => {
  const [isExpanded, setIsExpanded] = useState(() => expandedIds.has(folder.id));

  // Sync expansion when expandedIds changes (e.g. navigating to a folder from notification)
  useEffect(() => {
    if (expandedIds.has(folder.id)) {
      setIsExpanded(true);
    }
  }, [expandedIds, folder.id]);
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [contextMenuPos, setContextMenuPos] = useState({ x: 0, y: 0 });

  const hasChildren = children.length > 0;
  const isSelected = folder.id === selectedFolderId;

  const handleToggle = (e: React.MouseEvent) => { e.stopPropagation(); setIsExpanded(!isExpanded); };
  const handleSelect = () => onSelectFolder(folder.id);
  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault(); e.stopPropagation();
    const menuWidth = 220;
    const menuHeight = 320; // enough for all menu items including Review options
    const x = Math.min(e.clientX, window.innerWidth - menuWidth - 8);
    const y = Math.min(e.clientY, window.innerHeight - menuHeight - 8);
    setContextMenuPos({ x, y });
    setShowContextMenu(true);
  };
  const close = () => setShowContextMenu(false);

  return (
    <div className="folder-node-wrapper">
      <div
        className={`folder-node ${isSelected ? 'selected' : ''}`}
        style={{ paddingLeft: `${level * 20}px` }}
        onClick={handleSelect}
        onContextMenu={handleContextMenu}
      >
        <div className="folder-node-content">
          {hasChildren ? (
            <button className="folder-toggle" onClick={handleToggle} aria-label={isExpanded ? 'Collapse' : 'Expand'}>
              {isExpanded ? '▼' : '▶'}
            </button>
          ) : <span className="folder-spacer" />}
          <span className="folder-icon">{isExpanded && hasChildren ? '📂' : '📁'}</span>
          <span className="folder-name" title={folder.name}>
            {folder.name}
            {folder.folder_type === 'test_case' && folder.level > 0 && (() => {
              const count = getRecursiveCount(folder.id, allFolders, 'test_case_count');
              return count > 0 ? <span className="folder-count"> ({count})</span> : null;
            })()}
            {folder.folder_type === 'test_scenario' && folder.level > 0 && (() => {
              const count = getRecursiveCount(folder.id, allFolders, 'test_scenario_count');
              return count > 0 ? <span className="folder-count"> ({count})</span> : null;
            })()}
            {folder.review_status && folder.review_status !== 'Draft' && (() => {
              const badge = REVIEW_BADGE[folder.review_status];
              return badge ? (
                <span className="review-badge" style={{ background: badge.color }}>
                  {badge.label}
                </span>
              ) : null;
            })()}
          </span>
        </div>
      </div>

      {showContextMenu && (
        <>
          <div className="context-menu-overlay" onClick={close} />
          <div className="folder-context-menu" style={{ top: contextMenuPos.y, left: contextMenuPos.x }}>
            <button onClick={() => { onCreateSubfolder(folder.id); close(); }} className="context-menu-item">
              <span className="context-menu-icon">➕</span> Create Subfolder
            </button>
            <button onClick={() => { onRenameFolder(folder.id); close(); }} className="context-menu-item">
              <span className="context-menu-icon">✏️</span> Rename
            </button>
            {onMoveFolder && (
              <button onClick={() => { onMoveFolder(folder.id); close(); }} className="context-menu-item">
                <span className="context-menu-icon">📦</span> Move to...
              </button>
            )}
            {onSubmitReview && (folder.review_status === 'Draft' || folder.review_status === 'Changes Requested') &&
              folder.submitted_by === currentUserEmail && (
              <button onClick={() => { onSubmitReview(folder.id); close(); }} className="context-menu-item">
                <span className="context-menu-icon">📋</span> Submit for Review
              </button>
            )}
            {onSubmitReview && (folder.review_status === 'Draft' || folder.review_status === 'Changes Requested') &&
              !folder.submitted_by && (
              <button onClick={() => { onSubmitReview(folder.id); close(); }} className="context-menu-item">
                <span className="context-menu-icon">📋</span> Submit for Review
              </button>
            )}
            {onReviewAction && folder.review_status === 'In Review' &&
              (folder.reviewer_email === currentUserEmail) && (
              <button onClick={() => { onReviewAction(folder.id); close(); }} className="context-menu-item">
                <span className="context-menu-icon">✅</span> Review
              </button>
            )}
            {onSetEpicId && (
              <button onClick={() => { onSetEpicId(folder.id, folder.epic_id || ''); close(); }} className="context-menu-item">
                <span className="context-menu-icon">🔗</span>
                {folder.epic_id ? `Epic: ${folder.epic_id}` : 'Set Epic ID'}
              </button>
            )}
            <div className="context-menu-divider" />
            <button onClick={() => { onDeleteFolder(folder.id); close(); }} className="context-menu-item danger">
              <span className="context-menu-icon">🗑️</span> Delete
            </button>          </div>
        </>
      )}

      {isExpanded && hasChildren && (
        <div className="folder-children">
          {children.map(child => (
            <FolderNode
              key={child.id}
              folder={child}
              level={level + 1}
              selectedFolderId={selectedFolderId}
              onSelectFolder={onSelectFolder}
              onCreateSubfolder={onCreateSubfolder}
              onRenameFolder={onRenameFolder}
              onDeleteFolder={onDeleteFolder}
              onMoveFolder={onMoveFolder}
              onSubmitReview={onSubmitReview}
              onReviewAction={onReviewAction}
              onSetEpicId={onSetEpicId}
              currentUserEmail={currentUserEmail}
              children={allFolders.filter(f => f.parent_id === child.id)}
              allFolders={allFolders}
              expandedIds={expandedIds}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ── Move Modal ────────────────────────────────────────────────────────────────

interface MoveFolderModalProps {
  movingFolder: Folder;
  allFolders: Folder[];
  onConfirm: (newParentId: string | null) => void;
  onCancel: () => void;
}

const MoveFolderModal: React.FC<MoveFolderModalProps> = ({ movingFolder, allFolders, onConfirm, onCancel }) => {
  const [selectedParentId, setSelectedParentId] = useState<string | null>(movingFolder.parent_id || null);

  const renderOptions = (parentId: string | null, depth: number): React.ReactNode[] => {
    return allFolders
      .filter(f =>
        (f.parent_id || null) === parentId &&
        f.folder_type === movingFolder.folder_type &&
        f.id !== movingFolder.id &&
        !isDescendant(movingFolder.id, f.id, allFolders)
      )
      .flatMap(f => [
        <option key={f.id} value={f.id}>
          {'　'.repeat(depth)}📁 {f.name}
        </option>,
        ...renderOptions(f.id, depth + 1)
      ]);
  };

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal-content small" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Move "{movingFolder.name}"</h3>
          <button onClick={onCancel} className="modal-close">×</button>
        </div>
        <div className="modal-form">
          <div className="form-group">
            <label>Move to:</label>
            <select
              className="form-control"
              value={selectedParentId || ''}
              onChange={e => setSelectedParentId(e.target.value || null)}
              size={8}
              style={{ height: 'auto' }}
            >
              <option value="">📁 Root (top level)</option>
              {renderOptions(null, 0)}
            </select>
          </div>
          <div className="modal-actions" style={{ marginTop: '1rem' }}>
            <button className="btn btn-primary" onClick={() => onConfirm(selectedParentId)}>
              Move
            </button>
            <button className="btn btn-secondary" onClick={onCancel}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── FolderTree ────────────────────────────────────────────────────────────────

export const FolderTree: React.FC<FolderTreeProps> = ({
  folders, selectedFolderId, onSelectFolder,
  onCreateSubfolder, onRenameFolder, onDeleteFolder, onMoveFolder,
  onSubmitReview, onReviewAction, onSetEpicId, currentUserEmail,
  title = "Projects"
}) => {
  const [movingFolderId, setMovingFolderId] = useState<string | null>(null);

  const rootFolders = folders.filter(f => f.parent_id == null);
  const movingFolder = movingFolderId ? folders.find(f => f.id === movingFolderId) : null;

  // Auto-expand ancestors of the selected folder
  const expandedIds = getAncestorIds(selectedFolderId, folders);

  const handleMoveRequest = (folderId: string) => setMovingFolderId(folderId);

  const handleMoveConfirm = (newParentId: string | null) => {
    if (movingFolderId && onMoveFolder) {
      onMoveFolder(movingFolderId, newParentId);
    }
    setMovingFolderId(null);
  };

  return (
    <div className="folder-tree">
      <div className="folder-tree-header">
        <h3>📁 {title}</h3>
      </div>
      <div className="folder-tree-content">
        {rootFolders.length === 0 ? (
          <div className="folder-tree-empty">
            <p>No {title.toLowerCase()} found</p>
          </div>
        ) : rootFolders.map(folder => (
          <FolderNode
            key={folder.id}
            folder={folder}
            level={0}
            selectedFolderId={selectedFolderId}
            onSelectFolder={onSelectFolder}
            onCreateSubfolder={onCreateSubfolder}
            onRenameFolder={onRenameFolder}
            onDeleteFolder={onDeleteFolder}
            onMoveFolder={onMoveFolder ? handleMoveRequest : undefined}
            onSubmitReview={onSubmitReview}
            onReviewAction={onReviewAction}
            onSetEpicId={onSetEpicId}
            currentUserEmail={currentUserEmail}
            children={folders.filter(f => f.parent_id === folder.id)}
            allFolders={folders}
            expandedIds={expandedIds}
          />
        ))}
      </div>

      {movingFolder && (
        <MoveFolderModal
          movingFolder={movingFolder}
          allFolders={folders}
          onConfirm={handleMoveConfirm}
          onCancel={() => setMovingFolderId(null)}
        />
      )}
    </div>
  );
};
